"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { LiquidPill } from "./liquid-pill";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";

export interface PaginationProps {
  /** API v2 */
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  siblingCount?: number;
  className?: string;

  /** Compatibilidad con la API de rsuite usada en pantallas legacy */
  activePage?: number;
  total?: number;
  limit?: number;
  onChangePage?: (page: number) => void;
  maxButtons?: number;
  size?: "sm" | "md" | "lg" | string;
  prev?: boolean;
  next?: boolean;
  first?: boolean;
  last?: boolean;
  ellipsis?: boolean;
  boundaryLinks?: boolean;
  layout?: string[];
  locale?: Record<string, string>;
}

function generatePages(
  current: number,
  total: number,
  siblings: number,
  showEllipsis = true
): (number | "...")[] {
  if (!showEllipsis) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const pages: (number | "...")[] = [];
  const start = Math.max(2, current - siblings);
  const end = Math.min(total - 1, current + siblings);

  pages.push(1);
  if (start > 2) pages.push("...");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push("...");
  if (total > 1) pages.push(total);

  return pages;
}

function clampPage(page: number, totalPages: number) {
  return Math.min(Math.max(1, page), totalPages);
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount,
  className,
  activePage,
  total,
  limit,
  onChangePage,
  maxButtons,
  size,
  prev = true,
  next = true,
  ellipsis = true,
}: PaginationProps) {
  const computedTotalPages =
    totalPages ??
    (typeof total === "number" && typeof limit === "number" && limit > 0
      ? Math.ceil(total / limit)
      : 1);
  const safeTotalPages = Number.isFinite(computedTotalPages)
    ? Math.max(1, computedTotalPages)
    : 1;
  const safeCurrentPage = clampPage(
    currentPage ?? activePage ?? 1,
    safeTotalPages
  );
  const effectiveSiblingCount =
    siblingCount ??
    (typeof maxButtons === "number"
      ? Math.max(0, Math.floor((maxButtons - 3) / 2))
      : 1);
  const pillSize = size === "sm" ? "xs" : size === "lg" ? "md" : "sm";
  const changePage = onPageChange ?? onChangePage;

  if (safeTotalPages <= 1) return null;

  const pages = generatePages(
    safeCurrentPage,
    safeTotalPages,
    effectiveSiblingCount,
    ellipsis
  );

  const handlePageChange = (page: number) => {
    const nextPage = clampPage(page, safeTotalPages);
    if (nextPage === safeCurrentPage) return;
    changePage?.(nextPage);
  };

  return (
    <nav
      aria-label="Pagination"
      className={cn("flex flex-wrap items-center gap-1", className)}
    >
      {/* Prev */}
      {prev && (
        <LiquidPill
          as="button"
          type="button"
          shape="circle"
          size={pillSize}
          variant="slate"
          shadow="neutral"
          disabled={safeCurrentPage === 1}
          onClick={() => handlePageChange(safeCurrentPage - 1)}
          aria-label="Página anterior"
          icon={<ChevronLeft className="h-4 w-4" />}
        />
      )}

      {/* Pages */}
      {pages.map((page, i) =>
        page === "..." ? (
          <span
            key={`dots-${i}`}
            className="flex h-8 w-8 items-center justify-center"
          >
            <MoreHorizontal className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
          </span>
        ) : (
          <LiquidPill
            key={page}
            as="button"
            type="button"
            shape="circle"
            size={pillSize}
            variant="indigo"
            appearance={page === safeCurrentPage ? "tinted" : "normal"}
            shadow="neutral"
            onClick={() => handlePageChange(page as number)}
            aria-label={`Página ${page}`}
            aria-current={page === safeCurrentPage ? "page" : undefined}
          >
            <span className="text-xs font-medium leading-none">{page}</span>
          </LiquidPill>
        )
      )}

      {/* Next */}
      {next && (
        <LiquidPill
          as="button"
          type="button"
          shape="circle"
          size={pillSize}
          variant="slate"
          shadow="neutral"
          disabled={safeCurrentPage === safeTotalPages}
          onClick={() => handlePageChange(safeCurrentPage + 1)}
          aria-label="Página siguiente"
          icon={<ChevronRight className="h-4 w-4" />}
        />
      )}
    </nav>
  );
}
